import { useMemo, useState } from 'react';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import type { Connector } from 'types/transactions.types';
import {
  createConnectorChangeHandler,
  getMainAccountableBank,
} from '../../helpers';
import type { MoneyTransferInitialData } from '../types';
import { useAccountOptions } from './useAccountOptions';

export const useTransferState = (
  initialData?: MoneyTransferInitialData,
  isStandingOrderPayment?: boolean,
) => {
  const { data: connectors } = useGetConnectors();
  const mainAccount = getMainAccountableBank(connectors as Connector[]);

  const [sourceAccount, setSourceAccount] = useState<Connector | undefined>(
    typeof initialData?.sourceAccount !== 'string'
      ? initialData?.sourceAccount
      : undefined,
  );

  const [receiptAccount, setReceiptAccount] = useState<
    'external' | Connector | undefined
  >(initialData?.receiptAccount);

  const { accountableBanks, sourceAccountOptions, receiptAccountOptions } =
    useAccountOptions(sourceAccount, isStandingOrderPayment);

  // Find source account by name if provided in initial data
  const resolvedSourceAccount = useMemo(() => {
    if (
      typeof initialData?.sourceAccount === 'string' &&
      accountableBanks.length > 0
    ) {
      return accountableBanks.find(
        (connector) =>
          connector.bankAccountReference?.name === initialData.sourceAccount,
      );
    }
    if (sourceAccount?._id) return sourceAccount;
    return mainAccount;
  }, [
    initialData?.sourceAccount,
    accountableBanks,
    sourceAccount,
    mainAccount,
  ]);

  const isInternalTransfer = receiptAccount !== 'external';
  const canProceed = resolvedSourceAccount && receiptAccount;

  const insufficientBalance = useMemo(() => {
    return !!(
      initialData &&
      resolvedSourceAccount &&
      (initialData?.amount || 0) > (resolvedSourceAccount?.balance?.amount || 0)
    );
  }, [initialData, resolvedSourceAccount]);

  const handleSourceChange = (option: any) => {
    const changeHandler = createConnectorChangeHandler(
      accountableBanks,
      setSourceAccount,
    );
    changeHandler(option);

    // Reset recipient if it's the same as source
    if (receiptAccount !== 'external' && receiptAccount?._id === option.value) {
      setReceiptAccount('external');
    }
  };

  const handleRecipientChange = (option: any) => {
    if (option.value === 'external') {
      setReceiptAccount('external');
    } else {
      const connector = accountableBanks.find((c) => c._id === option.value);
      setReceiptAccount(connector);
    }
  };

  return {
    sourceAccount: resolvedSourceAccount,
    receiptAccount,
    sourceAccountOptions,
    receiptAccountOptions,
    isInternalTransfer,
    canProceed,
    insufficientBalance,
    handleSourceChange,
    handleRecipientChange,
  };
};
